<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="rrModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-scrollable rr-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="rrModalTitle">Reparaturbericht erstellen</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen" :disabled="submitting"></button>
        </div>

        <div class="modal-body">
          <!-- Ergebnis -->
          <div class="mb-3">
            <label class="form-label">Ergebnis</label>
            <div class="rr-results">
              <div class="form-check form-check-inline">
                <input id="rr-r1" class="form-check-input" type="radio" :value="1" v-model="repairStatus" />
                <label class="form-check-label" for="rr-r1">Repariert</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="rr-r0" class="form-check-input" type="radio" :value="0" v-model="repairStatus" />
                <label class="form-check-label" for="rr-r0">Nicht repariert</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="rr-r2" class="form-check-input" type="radio" :value="2" v-model="repairStatus" />
                <label class="form-check-label" for="rr-r2">Totalschaden</label>
              </div>
            </div>
          </div>

          <div class="mb-3 form-check">
            <input id="rr-intern" class="form-check-input" type="checkbox" v-model="isIntern" />
            <label class="form-check-label" for="rr-intern">Intern repariert</label>
          </div>

          <!-- Befund -->
          <div class="mb-3">
            <label class="form-label" for="rr-error">Befund</label>
            <select id="rr-error-tpl" class="form-select rr-phrase" v-model="errorTemplateId" @change="onErrorTemplate">
              <option :value="null">＋ Textbaustein einfügen …</option>
              <option v-for="t in errorTemplates" :key="t.id" :value="t.id">{{ t.description }}</option>
            </select>
            <textarea id="rr-error" v-model="errorText" class="form-control" rows="2"></textarea>
          </div>

          <!-- Durchgeführte Arbeiten -->
          <div class="mb-3">
            <label class="form-label" for="rr-work">Durchgeführte Arbeiten / Ergebnis</label>
            <select id="rr-work-tpl" class="form-select rr-phrase" v-model="workTemplateId" @change="onWorkTemplate">
              <option :value="null">＋ Textbaustein einfügen …</option>
              <option v-for="t in workTemplates" :key="t.id" :value="t.id">{{ t.description }}</option>
            </select>
            <textarea id="rr-work" v-model="repairWork" class="form-control" rows="4"></textarea>
            <button v-if="kvDiagnosis" type="button" class="btn btn-link btn-sm rr-kvbtn" @click="insertKvDiagnosis">
              KV-Diagnose einfügen
            </button>
          </div>

          <!-- Zusatzinformation -->
          <div class="mb-3">
            <label class="form-label" for="rr-extra">Zusatzinformation (optional)</label>
            <textarea id="rr-extra" v-model="extraInfo" class="form-control" rows="2"></textarea>
          </div>

          <!-- Tauschgerät: nur beim Austausch, dann Pflicht -->
          <div v-if="isSwap" class="mb-3">
            <label class="form-label" for="rr-swap">Tauschgerät IMEI/SN <span class="rr-required">*</span></label>
            <input id="rr-swap" v-model="swapImeiSn" type="text" class="form-control rr-swap-input" />
            <small class="rr-hint">Wird als Austausch-IMEI an Assona gemeldet.</small>
          </div>

          <div class="mb-3 form-check">
            <input id="rr-goodwill" class="form-check-input" type="checkbox" v-model="isGoodwill" />
            <label class="form-check-label" for="rr-goodwill">Kulanz</label>
          </div>

          <!-- Preise -->
          <div class="rr-price-grid">
            <div>
              <label class="form-label" for="rr-fee">Pauschale</label>
              <select id="rr-fee" v-model="feeId" class="form-select">
                <option :value="null">– keine –</option>
                <option v-for="f in fees" :key="f.id" :value="f.id">{{ f.description }} (€ {{ fmt(f.value) }})</option>
              </select>
            </div>
            <div>
              <label class="form-label" for="rr-costs">Eigenkosten Netto (€)</label>
              <input id="rr-costs" v-model="costs" type="number" min="0" step="0.01" class="form-control" />
            </div>
            <div>
              <label class="form-label" for="rr-brutto">Reparaturkosten Brutto (€)</label>
              <input id="rr-brutto" v-model="brutto" type="number" min="0" step="0.01" class="form-control" :readonly="isComplaint" />
              <small v-if="isComplaint" class="rr-netto">Reklamation — fest 0,00 € an Assona.</small>
              <small v-else class="rr-netto">Netto: {{ nettoText }} €</small>
            </div>
          </div>

          <div v-if="formError" class="error mt-2">{{ formError }}</div>
          <div v-if="submitError" class="error mt-2">{{ submitError }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">Abbrechen</button>
          <button type="button" class="btn btn-primary" :disabled="submitting" @click="onSubmit">
            {{ submitting ? 'Speichert…' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  createRepReport,
  listRepairFees,
  listRepReportTemplates,
  TEMPLATE_KEY_SWAP,
  TEMPLATE_TYPE_ERROR,
  TEMPLATE_TYPE_WORK,
  type RepReport,
  type RepReportTemplate,
  type RepairFee,
  type RepairStatus,
} from '../services/repReports';
import { listCostEstimates } from '../services/costEstimates';
import type { TicketDetail } from '../services/tickets';

/**
 * Formular für den Reparaturbericht. Aufruf via `open(ticket)`. Promise löst mit
 * dem erstellten Bericht (Erfolg) oder null (abgebrochen). KV-Prefill optional:
 * Brutto vom letzten KV, dessen Diagnose auf Knopfdruck einfügbar.
 */

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((r: RepReport | null) => void) | null = null;

const ticketId = ref<number>(0);
const fees     = ref<RepairFee[]>([]);
const kvDiagnosis = ref<string>('');

// Textbausteine kommen aus handyb_r2.rep_report_templates (früher hartkodiert).
const errorTemplates = ref<RepReportTemplate[]>([]);
const workTemplates  = ref<RepReportTemplate[]>([]);
const errorTemplateId = ref<number | null>(null);
const workTemplateId  = ref<number | null>(null);

const repairStatus = ref<RepairStatus | null>(1);
const isIntern     = ref<boolean>(true);
const repairWork   = ref<string>('');
const errorText    = ref<string>('');
const extraInfo    = ref<string>('');
const swapImeiSn   = ref<string>('');
const isGoodwill   = ref<boolean>(false);
const feeId        = ref<number | null>(null);
const costs        = ref<number | string>('');
const brutto       = ref<number | string>('');
/** Reklamation: Betrag fest 0, der Server erzwingt das ebenfalls. */
const isComplaint  = ref<boolean>(false);

const formError   = ref<string | null>(null);
const submitError = ref<string | null>(null);
const submitting  = ref<boolean>(false);

let result: RepReport | null = null;

const bruttoNum = computed(() => {
  const raw = typeof brutto.value === 'number' ? brutto.value : Number.parseFloat(String(brutto.value ?? '').replace(',', '.'));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
});
const nettoText = computed(() => fmt(Math.round((bruttoNum.value / 1.2) * 100) / 100));

function fmt(v: number): string {
  return v.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Austausch: allein die Auswahl des SWAP-Bausteins entscheidet — nicht der Text.
 * Der Server prüft dasselbe anhand von work_template_id.
 */
const isSwap = computed(() =>
  workTemplates.value.find(t => t.id === workTemplateId.value)?.template_key === TEMPLATE_KEY_SWAP
);

/** Baustein anhängen statt ersetzen — bereits Getipptes bleibt erhalten. */
function appendText(target: 'error' | 'work', text: string): void {
  if (!text) return;
  const current = target === 'error' ? errorText.value : repairWork.value;
  const merged  = current ? `${current}\n${text}` : text;
  if (target === 'error') errorText.value = merged; else repairWork.value = merged;
}

function onErrorTemplate(): void {
  const tpl = errorTemplates.value.find(t => t.id === errorTemplateId.value);
  if (!tpl) return;
  appendText('error', tpl.text);
  // Auswahl zurücksetzen, damit derselbe Baustein erneut einfügbar ist.
  errorTemplateId.value = null;
}

/**
 * Anders als beim Befund bleibt die Auswahl hier stehen: sie ist nicht nur
 * Texteinfügung, sondern trägt mit SWAP eine fachliche Aussage und wird
 * als work_template_id mitgeschickt.
 */
function onWorkTemplate(): void {
  const tpl = workTemplates.value.find(t => t.id === workTemplateId.value);
  if (!tpl) return;
  appendText('work', tpl.text);
}

function insertKvDiagnosis(): void {
  if (!kvDiagnosis.value) return;
  repairWork.value = repairWork.value ? `${repairWork.value}\n${kvDiagnosis.value}` : kvDiagnosis.value;
}

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: false });
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

async function open(ticket: TicketDetail): Promise<RepReport | null> {
  ticketId.value = ticket.id;

  repairStatus.value = 1;
  isIntern.value     = true;
  repairWork.value   = '';
  errorText.value    = '';
  errorTemplateId.value = null;
  workTemplateId.value  = null;
  extraInfo.value    = '';
  swapImeiSn.value   = '';
  isGoodwill.value   = false;
  feeId.value        = null;
  costs.value        = '';
  isComplaint.value  = ticket.is_complaint;
  brutto.value       = ticket.is_complaint ? 0 : '';
  kvDiagnosis.value  = '';
  formError.value    = null;
  submitError.value  = null;
  submitting.value   = false;
  result             = null;

  if (fees.value.length === 0) {
    try { fees.value = await listRepairFees(); } catch { /* Dropdown bleibt leer */ }
  }

  if (errorTemplates.value.length === 0 && workTemplates.value.length === 0) {
    try {
      const all = await listRepReportTemplates();
      errorTemplates.value = all.filter(t => t.template_type_id === TEMPLATE_TYPE_ERROR);
      workTemplates.value  = all.filter(t => t.template_type_id === TEMPLATE_TYPE_WORK);
    } catch { /* Dropdowns bleiben leer, Freitext geht weiter */ }
  }

  // KV-Prefill: letzten KV holen → Brutto vorbelegen, Diagnose als Einfüge-Option.
  try {
    const kvs = await listCostEstimates(ticket.id);
    if (kvs.length > 0) {
      const last = kvs[0]; // neueste zuerst
      if (last.bru_price !== null && !isComplaint.value) brutto.value = last.bru_price;
      kvDiagnosis.value = last.error_description ?? '';
    }
  } catch { /* kein KV / nicht abrufbar → ohne Prefill */ }

  return new Promise<RepReport | null>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

async function onSubmit(): Promise<void> {
  if (submitting.value) return;
  formError.value = null;

  if (repairStatus.value === null) {
    formError.value = 'Bitte ein Ergebnis wählen.';
    return;
  }
  const befund = errorText.value.trim();
  const work   = repairWork.value.trim();
  const extra  = extraInfo.value.trim();
  const repairReport = [befund, work, extra].filter(Boolean).join('\n\n');
  if (repairReport === '') {
    formError.value = 'Bitte den Befund oder die durchgeführten Arbeiten angeben.';
    return;
  }

  // Ohne Austausch-IMEI fehlt in der Assona-Meldung das exchangeIMEI.
  if (isSwap.value && swapImeiSn.value.trim() === '') {
    formError.value = 'Bei einem Austausch ist die IMEI/SN des Tauschgeräts erforderlich.';
    return;
  }

  const costsNum = typeof costs.value === 'number'
    ? costs.value
    : Number.parseFloat(String(costs.value ?? '').replace(',', '.'));

  submitting.value  = true;
  submitError.value = null;
  try {
    result = await createRepReport(ticketId.value, {
      repair_status:      repairStatus.value,
      repair_report:      repairReport,
      is_intern_repaired: isIntern.value,
      is_goodwill:        isGoodwill.value,
      work_template_id:   workTemplateId.value,
      swap_imei_sn:       swapImeiSn.value.trim() || undefined,
      brutto:             bruttoNum.value > 0 ? bruttoNum.value : null,
      costs:              Number.isFinite(costsNum) && costsNum >= 0 ? Math.round(costsNum * 100) / 100 : null,
      repair_fee_id:      feeId.value,
    });
    modal?.hide();
  } catch (e) {
    submitError.value = e instanceof Error ? `Speichern fehlgeschlagen: ${e.message}` : 'Speichern fehlgeschlagen.';
    submitting.value  = false;
  }
}

function onHidden(): void {
  resolver?.(result);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.rr-modal { max-width: 640px; }
.rr-modal :deep(.modal-title)   { font-size: 16px; }
.rr-modal :deep(.modal-body)    { padding: 0.75rem 1rem; }
.rr-modal :deep(.mb-3)          { margin-bottom: 0.6rem !important; }
.rr-modal :deep(.form-label)    { margin-bottom: 0.2rem; font-size: 12.5px; font-weight: 500; }
.rr-modal :deep(.form-control),
.rr-modal :deep(.form-select)   { font-size: 13px; padding: 0.25rem 0.5rem; }
.rr-modal :deep(.form-check-label) { font-size: 13px; }

.rr-results { display: flex; flex-wrap: wrap; gap: 4px 16px; }
.rr-phrase { margin-bottom: 6px; }
.rr-kvbtn { padding: 2px 0 0; }
.rr-swap-input { max-width: 260px; }
.rr-required { color: var(--bs-danger, #dc3545); }
.rr-hint { color: var(--muted, #6c757d); font-size: 12px; }
.rr-netto { color: var(--muted, #6c757d); }
.rr-price-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
</style>
